import { OrganizerInviteShareForm } from './OrganizerInviteShareForm';
import { sameOriginUrl } from '@src/routing/urls';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CardInfo } from '@lub0v/santa-utils';
import { Button, Select, Stack, Typography, HintError, Input, closeDialog } from '@src/components';

export function AddOrganizerForm({ boxKey, cards, onChanged }: { boxKey: string; cards: CardInfo[]; onChanged: () => Promise<void> | void }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState('');
  const [name, setName] = useState('');
  const [invite, setInvite] = useState<{ id: number; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    if (!selected || busy) return;
    setBusy(true); setError(null);
    try {
      const response = await fetch(`/api/box/${encodeURIComponent(boxKey)}/organizers/${selected}`, { method: 'POST', credentials: 'same-origin' });
      if (!response.ok) throw new Error('promotion failed');
      await onChanged();
      closeDialog();
    } catch { setError(t('organizersPanel.saveFailed')); }
    finally { setBusy(false); }
  }
  async function createInvite() {
    if (name.trim().length < 2 || busy) return;
    setBusy(true); setError(null);
    try {
      const response = await fetch(`/api/box/${encodeURIComponent(boxKey)}/organizers/new`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username: name.trim() }),
      });
      if (!response.ok) throw new Error('Invite failed');
      const invitation: { id: number; url: string } = await response.json();
      setInvite({ ...invitation, url: sameOriginUrl(invitation.url) });
      await onChanged();
    } catch { setError(t('organizersPanel.saveFailed')); }
    finally { setBusy(false); }
  }
  if (invite) return <OrganizerInviteShareForm boxKey={boxKey} organizer={{ id: invite.id, username: name.trim() }} initialLink={invite.url} />;
  return <Stack gap={6}>
    <Stack gap={3}>
      <Typography as="h3" role="title/card">{t('organizersPanel.existing')}</Typography>
      <Typography as="p" role="body/sm">{t('organizersPanel.permissions')}</Typography>
      <Select label={t('organizersPanel.selectLabel')} value={selected} onValueChange={setSelected}
        options={cards.map(card => ({ value: String(card.id), label: card.username }))}
        placeholder={t(cards.length ? 'organizersPanel.selectPlaceholder' : 'organizersPanel.noCandidates')}
        disabled={busy || !cards.length} testId="organizer-select" />
      <HintError error={error} />
      <Button disabled={!selected || busy} loading={busy} onClick={() => void save()} testId="organizer-add-save">{t('organizersPanel.add')}</Button>
    </Stack>
    <Stack gap={2}>
      <Typography as="h3" role="title/card">{t('organizersPanel.inviteNew')}</Typography>
      <Typography as="p" role="body/base">{t('organizersPanel.organizerOnlyHint')}</Typography>
      <Input label={t('organizersPanel.inviteeName')} value={name} onValueChange={setName} maxLength={64} disabled={busy} testId="new-organizer-name" />
      <HintError error={error} />
      <Button disabled={name.trim().length < 2 || busy} loading={busy} onClick={() => void createInvite()} testId="new-organizer-next">{t('organizersPanel.next')}</Button>
    </Stack>
  </Stack>;
}
